import React, { ChangeEvent } from 'react';
import { SavedIcon, Spinner, Switch } from 'evergreen-ui';
import AppButton from 'components/AppButton/AppButton';
import { EmailsForm } from '../../../../components/EmailsForm/EmailsForm';
import { TabPanel } from '../components/TabPanel';
import { SettingRow } from '../components/SettingRow';
import styles from '../TargetGroupSettings.module.css';

interface NotificationsTabProps {
    tab: string;
    index: number;
    selectedIndex: number;

    scanCompletionNotification: boolean;
    setScanCompletionNotification: (value: boolean) => void;
    scanCompletionNotificationEmails: string;
    isScanCompletionNotificationEmailsValid: boolean;
    onScanCompletionEmailsChange: (emails: string, isValid: boolean) => void;

    reportGenerationNotification: boolean;
    setReportGenerationNotification: (value: boolean) => void;
    reportGenerationNotificationEmails: string;
    isReportGenerationNotificationEmailsValid: boolean;
    onReportGenerationEmailsChange: (emails: string, isValid: boolean) => void;

    isLoading: boolean;
    displaySuccess: boolean;
    onSave: () => void;
}

/**
 * Notifications tab — opt-in toggles + email recipient lists for scan completion
 * and report generation events. The Switch on each row uses the raw evergreen-ui
 * onChange directly because the EmailsForm shares the same toggle state via
 * `setToggle`. The save handler scopes its PUT body to notification fields only.
 */
export const NotificationsTab = ({
    tab,
    index,
    selectedIndex,
    scanCompletionNotification,
    setScanCompletionNotification,
    scanCompletionNotificationEmails,
    isScanCompletionNotificationEmailsValid,
    onScanCompletionEmailsChange,
    reportGenerationNotification,
    setReportGenerationNotification,
    reportGenerationNotificationEmails,
    isReportGenerationNotificationEmailsValid,
    onReportGenerationEmailsChange,
    isLoading,
    displaySuccess,
    onSave,
}: NotificationsTabProps) => (
    <TabPanel tab={tab} index={index} selectedIndex={selectedIndex}>
        <table
            className={
                styles.target_group_settings__pane__configuration__notifications_table
            }>
            <colgroup>
                <col className={styles.setting_key} />
                <col className={styles.setting_value} />
            </colgroup>
            <tbody>
                <SettingRow label="Scan completion notification">
                    <Switch
                        height={24}
                        checked={scanCompletionNotification}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            setScanCompletionNotification(e.target.checked)
                        }
                        className={styles.switch}
                    />
                </SettingRow>

                <SettingRow
                    rowStyle={{ verticalAlign: 'top' }}
                    label="Scan completion notification emails"
                    description="List of emails that will receive notification when a scan is completed">
                    <EmailsForm
                        onEmailsChange={onScanCompletionEmailsChange}
                        initialEmails={scanCompletionNotificationEmails}
                        emailsDisabled={!scanCompletionNotification}
                        setToggle={setScanCompletionNotification}
                    />
                </SettingRow>

                <SettingRow label="Report generation notification">
                    <Switch
                        height={24}
                        checked={reportGenerationNotification}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            setReportGenerationNotification(e.target.checked)
                        }
                        className={styles.switch}
                    />
                </SettingRow>

                <SettingRow
                    rowStyle={{ verticalAlign: 'top' }}
                    label="Report generation notification emails"
                    description="List of emails that will receive notification when a report is generated">
                    <EmailsForm
                        onEmailsChange={onReportGenerationEmailsChange}
                        initialEmails={reportGenerationNotificationEmails}
                        emailsDisabled={!reportGenerationNotification}
                        setToggle={setReportGenerationNotification}
                    />
                </SettingRow>
            </tbody>
        </table>
        <div className={styles.submit_wrap}>
            <span className={styles.save_result}>
                {isLoading ? <Spinner size={15} /> : displaySuccess ? 'Saved!' : ''}
            </span>
            <AppButton
                icon={SavedIcon}
                onClick={onSave}
                width={'120px'}
                isDisabled={
                    isLoading ||
                    (scanCompletionNotification &&
                        !isScanCompletionNotificationEmailsValid) ||
                    (reportGenerationNotification &&
                        !isReportGenerationNotificationEmailsValid)
                }>
                Save
            </AppButton>
        </div>
    </TabPanel>
);

export default NotificationsTab;
